import { useTheme } from '@emotion/react'
import { tokens } from '@news-mono/design-tokens'
import {
    AddToCartEvent,
    AppState,
    setDismissedSubscriptionPrompt,
    sswPackages,
    SubscriptionPromptCookieData,
} from '@news-mono/web-common'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { getAttribution } from '../../../../errors/ErrorPage'
import { TheWestMark } from '../../../../logos/TheWestMark/TheWestMark'
import { TheWestHeaderCloseIcon } from '../../../../navigation/TheWestHeaderDropdown/icons/TheWestHeaderCloseIcon'
import CountdownInactivity from '../../../../tools/CountdownInactivity'
import { useAuthActions } from '../../../authentication'
import { getSubscriptionValue } from '../../../breach/common'
import { createLinkClickEvent } from '../../createLinkClickHandler'
import { useBreachWallConfig } from '../BreachNotificationWrapper/BreachNotificationWrapper'
import { MiniSubscriptionPackage } from './MiniSubscriptionPackage'
import { StyledSubscriptionPackageButton2022 } from './SubscriptionPackage.styled'
import {
    StyledSubscriptionPrompt,
    StyledSubscriptionPromptCloseButton,
    StyledSubscriptionPromptMenuBar,
} from './SubscriptionPrompt.styled'

const FIVE_MINUTES = 1000 * 60 * 5
const IDLE_TIME_IN_SECONDS = 120
const IS_SHOWING_LOCAL_STORAGE_KEY = 'SubscriptionPromptIsShowing'

export const SubscriptionPrompt = ({
    promptData,
    checkoutLink,
}: {
    promptData: SubscriptionPromptCookieData
    checkoutLink: string
}) => {
    const [isHidden, setIsHidden] = React.useState(
        window?.localStorage?.getItem(IS_SHOWING_LOCAL_STORAGE_KEY) !=
            true.toString(),
    )
    const [hasAlreadyPrompted, setHasAlreadyPrompted] = React.useState(false)
    const { onSubscribeClick } = useAuthActions()
    const theme = useTheme()

    const dismissedPrompt = useSelector(
        (state: AppState) => state.misc.dismissedSubscriptionPrompt,
    )
    const dispatch = useDispatch()
    const startDateTime = promptData.time + FIVE_MINUTES // Don't prompt until at least 5 minutes after the user abandoned their subscription.
    const waitTimeUntilListenForInactivityInSeconds =
        Math.max(0, startDateTime - new Date().valueOf()) / 1000

    const isAlreadyDismissed = React.useCallback(() => {
        return (
            (dismissedPrompt?.href === promptData.href &&
                dismissedPrompt?.time === promptData.time) ||
            hasAlreadyPrompted
        )
    }, [
        dismissedPrompt?.href,
        dismissedPrompt?.time,
        promptData.href,
        promptData.time,
        hasAlreadyPrompted,
    ])
    const navigateAwayHandler = React.useCallback(
        (e: BeforeUnloadEvent) => {
            if (!isAlreadyDismissed()) {
                setTimeout(show, 10)
                e.preventDefault()
                e.returnValue =
                    'You have an incomplete subscription. Are you sure you wish to leave without completing it?'
                return e.returnValue
            }
        },
        [isAlreadyDismissed],
    )
    const hide = React.useCallback(() => {
        setIsHidden(true)
        window?.localStorage?.setItem(
            IS_SHOWING_LOCAL_STORAGE_KEY,
            false.toString(),
        )
        dispatch(setDismissedSubscriptionPrompt(promptData))
    }, [setIsHidden, dispatch, promptData.href, promptData.time])
    const show = React.useCallback(() => {
        setIsHidden(false)
        setHasAlreadyPrompted(true)
    }, [setIsHidden, setHasAlreadyPrompted])
    const hideOnEscape = React.useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                hide()
            }
        },
        [hide],
    )

    React.useEffect(() => {
        addAndModifyFakeInput()

        const rootElement = document.getElementById('root')
        rootElement?.setAttribute('disabled', (!isHidden).toString())

        if (isHidden) {
            document.body.style.overflowY = 'auto'
            document.body.removeEventListener('keyup', hideOnEscape)
            window.addEventListener('beforeunload', navigateAwayHandler, {
                capture: true,
            })
            window?.localStorage?.setItem(
                IS_SHOWING_LOCAL_STORAGE_KEY,
                false.toString(),
            )
        } else {
            document.body.style.overflowY = 'hidden'
            document.body.addEventListener('keyup', hideOnEscape)
            window.removeEventListener('beforeunload', navigateAwayHandler, {
                capture: true,
            })
            window?.localStorage?.setItem(
                IS_SHOWING_LOCAL_STORAGE_KEY,
                true.toString(),
            )
        }

        return function cleanUp() {
            rootElement?.removeAttribute('disabled')
            document.body.style.overflowY = 'auto'
            document.body.removeEventListener('keyup', hideOnEscape)
            window.removeEventListener('beforeunload', navigateAwayHandler, {
                capture: true,
            })
        }
    }, [isHidden, navigateAwayHandler, hideOnEscape])

    const packageInfo =
        promptData.package_info?.package ||
        sswPackages.find((x) => x.path === promptData.href) ||
        sswPackages[0]
    const { deal, creativeName } = useBreachWallConfig(useLocation())
    const addToCartPayload: AddToCartEvent['payload'] = promptData?.package_info
        ?.cart_payload || {
        currency: 'AUD',
        item_id: packageInfo.stdRateCode,
        item_brand:
            getAttribution(theme.kind, theme.section) || 'The West Australian',
        item_name: packageInfo.title,
        price: getSubscriptionValue(packageInfo.weeklyPrice),
        quantity: 1,
        coupon: undefined,
        discount: getSubscriptionValue(0),
        item_variant: 'native',
    }
    return (
        <StyledSubscriptionPrompt
            style={{ display: isHidden ? 'none' : undefined }}
            hidden={isHidden}
            aria-hidden={isHidden}
        >
            <CountdownInactivity
                timeToInactive={waitTimeUntilListenForInactivityInSeconds}
                timeRequiredInactive={IDLE_TIME_IN_SECONDS}
                onEvent={() => {
                    if (!isAlreadyDismissed()) {
                        show()
                    }
                }}
            />
            {!isHidden && (
                <div>
                    <StyledSubscriptionPromptMenuBar>
                        <TheWestMark height={36} />
                        <StyledSubscriptionPromptCloseButton onClick={hide}>
                            <TheWestHeaderCloseIcon
                                fill={tokens.thewest.colors.palette.greyQuokka}
                            />
                        </StyledSubscriptionPromptCloseButton>
                    </StyledSubscriptionPromptMenuBar>
                    <h2>Are you still there?</h2>
                    <p>We've saved your subscription progress.</p>
                    <MiniSubscriptionPackage
                        packageInfo={packageInfo}
                        offerPrice={
                            promptData?.package_info?.is_offer
                                ? promptData.package_info?.offer_price
                                : undefined
                        }
                    />
                    <StyledSubscriptionPackageButton2022
                        packageTheme="default"
                        style={{ maxWidth: 252 }}
                        onClick={() => {
                            createLinkClickEvent(
                                creativeName,
                                deal,
                                () => null,
                                undefined,
                                1,
                                addToCartPayload,
                            )()
                            onSubscribeClick()
                        }}
                    >
                        Continue to checkout
                    </StyledSubscriptionPackageButton2022>
                </div>
            )}
        </StyledSubscriptionPrompt>
    )
}

function addAndModifyFakeInput() {
    // Hack to make the prmompt more likely to show up as some browsers only activate it once the user edits an input.
    const input = document.createElement('input')
    input.value = 'I changed something'
    input.style.display = 'none'
    document.body.appendChild(input)
    input.value = 'Different'
    input.dispatchEvent(
        new InputEvent('input', { view: window, inputType: 'keyboard' }),
    )
}
